// PrivacyPage.jsx
// Public route — no auth required.
// Route: /vocab/privacy

import { Mascot } from "@/components/styleguide/Mascot";

export default function PrivacyPage() {
  return (
    <div className="min-h-[100svh] bg-background">
      {/* ── Header ── */}
      <header className="sticky top-0 z-10 border-b border-border bg-background/90 backdrop-blur-sm">
        <div className="mx-auto flex max-w-2xl items-center justify-between px-5 py-3">
          <a
            href="/vocab"
            className="flex items-center gap-2"
            aria-label="Back to Bloom home"
          >
            <Mascot size={30} animated={false} />
            <span className="font-display text-lg font-black tracking-tight text-foreground">
              Bloom
            </span>
          </a>
          <a
            href="/vocab"
            className="text-xs font-bold text-[#FF7A45] underline underline-offset-2 transition hover:opacity-75"
          >
            ← Back to Bloom
          </a>
        </div>
      </header>

      {/* ── Content ── */}
      <main className="mx-auto max-w-2xl px-5 py-10 sm:py-14">
        {/* Title */}
        <div className="mb-8 border-b border-border pb-6">
          <p className="mb-1 text-xs font-bold uppercase tracking-widest text-[#FF7A45]">
            Legal
          </p>
          <h1 className="font-display text-3xl font-black text-foreground sm:text-4xl">
            Privacy Policy
          </h1>
          <p className="mt-2 text-sm font-semibold text-muted-foreground">
            Last updated: October 2026
          </p>
        </div>

        <div className="space-y-8 text-sm font-semibold leading-relaxed text-muted-foreground">

          {/* Intro */}
          <section>
            <p>
              Welcome to Bloom. We're a small team at We Built Here, and we built Bloom to
              help children in Years 3–6 develop their vocabulary in a fun, engaging and
              stress-free way.
            </p>
            <p className="mt-3">
              Your privacy, and your child's privacy, is important to us. This Privacy
              Policy explains what information we collect, how we use it, who we share it
              with and the choices available to you. We've written it in plain English so
              parents and guardians can understand how Bloom handles their family's
              information.
            </p>
          </section>

          {/* 1. About */}
          <section>
            <H2>1. About Bloom</H2>
            <p>
              Bloom is a vocabulary-learning application developed and operated by{" "}
              <strong className="text-foreground">Rohan Solutions Ltd</strong>, trading as{" "}
              <strong className="text-foreground">We Built Here</strong>, a company
              registered in England and Wales.
            </p>
            <p className="mt-3">
              In this policy, "Bloom", "we", "us" and "our" refer to Rohan Solutions Ltd.
              We are responsible for deciding how personal information is used in connection
              with Bloom.
            </p>
            <p className="mt-3">
              If you have any questions about this Privacy Policy, contact us at{" "}
              <EmailLink />.
            </p>
          </section>

          {/* 2. What we collect */}
          <section>
            <H2>2. What information we collect</H2>
            <p className="mb-4">
              We aim to collect only the information reasonably needed to provide and
              improve Bloom.
            </p>

            <H3>Account information</H3>
            <p className="mb-4">
              When you sign in using Google, we receive your name and email address from
              Google, as permitted by the sign-in process. We use this information to
              identify your account and provide access to Bloom.
            </p>

            <H3>Child profile information</H3>
            <p className="mb-4">
              Parents and guardians can create profiles for children using Bloom. This may
              include a child's name or nickname, school year and avatar or profile choice.
              You do not need to use your child's real name — we encourage parents to
              consider using a nickname.
            </p>

            <H3>Learning activity and progress</H3>
            <p className="mb-4">
              Bloom may record learning activity, including words practised, quiz answers,
              scores and progress. We use this information to show learning progress and
              provide appropriate vocabulary-learning activities.
            </p>

            <H3>Subscription information</H3>
            <p className="mb-4">
              We record whether your account is on a free trial or a paid plan. Payments
              are processed by Stripe — we do not store your full payment card details on
              Bloom's servers.
            </p>

            <H3>Technical information</H3>
            <p className="mb-4">
              Our systems may generate technical information such as server logs, error
              messages and request timestamps to maintain security and reliability. Technical
              logs are deleted automatically after 30 days, subject to any applicable legal
              requirements.
            </p>

            <H3>Information we do not intentionally collect</H3>
            <p>
              Bloom is not designed to collect children's home addresses, precise location
              data or sensitive personal information as part of its ordinary vocabulary-learning
              features. Please do not enter unnecessary personal or sensitive information into
              child profiles or other free-text fields.
            </p>
          </section>

          {/* 3. How we use */}
          <section>
            <H2>3. How we use your information</H2>
            <ul className="space-y-3">
              <Li>
                <strong className="text-foreground">Account management</strong> — to sign
                you in and manage your account.
              </Li>
              <Li>
                <strong className="text-foreground">Child profiles</strong> — to display
                the profiles you create and manage.
              </Li>
              <Li>
                <strong className="text-foreground">Learning</strong> — to record progress,
                deliver vocabulary activities and show learning results.
              </Li>
              <Li>
                <strong className="text-foreground">Subscriptions</strong> — to manage
                access to free-trial and paid features.
              </Li>
              <Li>
                <strong className="text-foreground">Security and reliability</strong> — to
                diagnose technical problems, protect the service and prevent misuse.
              </Li>
              <Li>
                <strong className="text-foreground">Customer support</strong> — to respond
                to questions and requests.
              </Li>
              <Li>
                <strong className="text-foreground">Marketing</strong> — to send optional
                Bloom news, tips and updates where we have the appropriate permission or
                other lawful basis.
              </Li>
            </ul>
            <p className="mt-3">
              We do not sell personal information to advertisers, and we do not use
              children's personal information for third-party advertising.
            </p>
          </section>

          {/* 4. Children */}
          <section>
            <H2>4. Children's information and parental control</H2>
            <p>
              Bloom is designed for parents and guardians to manage children's
              vocabulary-learning profiles. Parents and guardians are responsible for
              creating and managing child profiles under their accounts.
            </p>
            <p className="mt-3">
              You can use a nickname instead of a child's real name — we do not require
              a child's real name for the profile to function. We do not intentionally
              request children's email addresses or precise location data for ordinary
              learning activities.
            </p>
            <p className="mt-3">
              We use children's learning information to provide the learning features
              described in this policy, including displaying progress and selecting
              appropriate learning activities. Parents can manage and delete child profiles
              through their account settings.
            </p>
            <p className="mt-3">
              We handle children's information in accordance with applicable UK data
              protection law and the UK Information Commissioner's Office guidance on
              children's privacy and age-appropriate design.
            </p>
          </section>

          {/* 5. Lawful basis */}
          <section>
            <H2>5. Our lawful basis for using personal information</H2>
            <p className="mb-4">
              Under UK data protection law, we must have a lawful basis for processing
              personal information. Depending on the information and purpose, we may rely
              on the following:
            </p>
            <ul className="space-y-3">
              <Li>
                <strong className="text-foreground">Contract</strong> — where processing is
                necessary to provide Bloom, manage an account or deliver a subscription.
              </Li>
              <Li>
                <strong className="text-foreground">Legitimate interests</strong> — where we
                have a genuine and proportionate interest in maintaining security and keeping
                Bloom reliable, provided those interests do not override your rights.
              </Li>
              <Li>
                <strong className="text-foreground">Consent</strong> — where we rely on your
                permission, for example for certain marketing communications. You can withdraw
                consent at any time, without affecting any processing carried out lawfully
                before withdrawal.
              </Li>
              <Li>
                <strong className="text-foreground">Legal obligation</strong> — where we must
                retain or disclose information to comply with applicable law.
              </Li>
            </ul>
          </section>

          {/* 6. Sharing */}
          <section>
            <H2>6. Who we share information with</H2>
            <p className="mb-4">
              We do not sell your personal information. Bloom relies on a small number of
              trusted service providers to operate.
            </p>

            <H3>Google</H3>
            <p className="mb-4">
              Google provides the sign-in service used to authenticate your account and
              supplies your name and email address. Google's own{" "}
              <ExternalLink href="https://policies.google.com/privacy">
                Privacy Policy
              </ExternalLink>{" "}
              applies to how Google handles your data.
            </p>

            <H3>Stripe</H3>
            <p className="mb-4">
              Stripe processes subscription payments. Your card details go directly to
              Stripe and are never stored on our servers. Stripe's{" "}
              <ExternalLink href="https://stripe.com/gb/privacy">Privacy Policy</ExternalLink>{" "}
              applies.
            </p>

            <H3>Hosting, database and technical service providers</H3>
            <p className="mb-4">
              We use service providers to host Bloom, store information and support its
              technical operation. These providers may process personal information on our
              behalf where necessary to provide their services.
            </p>

            <H3>Email and support providers</H3>
            <p>
              Where applicable, we may use email or customer-support services to respond to
              enquiries or send communications you have requested or agreed to receive. We
              take reasonable steps to ensure that providers handling personal information
              are subject to appropriate contractual and security safeguards.
            </p>
          </section>

          {/* 7. International transfers */}
          <section>
            <H2>7. International transfers</H2>
            <p>
              Some of our service providers may store or process information outside the
              United Kingdom. Where personal information is transferred internationally,
              we will take the steps required by applicable UK data protection law, which
              may include using an adequacy regulation or appropriate safeguards. Contact
              us at <EmailLink /> if you would like further information about relevant
              transfer safeguards.
            </p>
          </section>

          {/* 8. Retention */}
          <section>
            <H2>8. How long we keep your information</H2>
            <p className="mb-4">
              We keep personal information only for as long as reasonably necessary for the
              purposes described in this policy, subject to legal requirements.
            </p>
            <ul className="space-y-3">
              <Li>
                <strong className="text-foreground">Active accounts</strong> — account
                information and children's learning progress are retained while the account
                remains active and the information is needed to provide Bloom.
              </Li>
              <Li>
                <strong className="text-foreground">Deleted accounts</strong> — information
                is removed from our active systems within 90 days, unless it must be
                retained for legal, security or other legitimate reasons.
              </Li>
              <Li>
                <strong className="text-foreground">Deleted child profiles</strong> —
                information is removed in accordance with our deletion process and applicable
                retention requirements.
              </Li>
              <Li>
                <strong className="text-foreground">Payment and accounting records</strong>{" "}
                — relevant transaction records may be retained for up to seven years where
                necessary to meet applicable tax, accounting or legal obligations. This does
                not mean we retain your full card details.
              </Li>
              <Li>
                <strong className="text-foreground">Technical logs</strong> — deleted
                automatically after 30 days, subject to applicable security or legal
                requirements.
              </Li>
            </ul>
          </section>

          {/* 9. Rights */}
          <section>
            <H2>9. Your data protection rights</H2>
            <p className="mb-3">
              Depending on the circumstances, you may have the right to:
            </p>
            <ul className="space-y-2">
              {[
                "Access personal information we hold about you",
                "Request correction of inaccurate or incomplete information",
                'Request deletion of personal information ("right to be forgotten")',
                "Request restriction of certain processing",
                "Object to processing based on legitimate interests or direct marketing, where applicable",
                "Request a portable copy of certain information",
                "Withdraw consent where we rely on consent",
              ].map((right) => (
                <li key={right} className="flex gap-2">
                  <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[#FF7A45]" />
                  {right}
                </li>
              ))}
            </ul>
            <p className="mt-3">
              These rights are subject to applicable legal conditions and exceptions. To
              exercise any of them, email us at <EmailLink />. We will normally respond
              within one month.
            </p>
            <p className="mt-3">
              If you are unhappy with how we handle your personal information, you can
              complain to the UK Information Commissioner's Office (ICO) at{" "}
              <ExternalLink href="https://ico.org.uk/make-a-complaint/">
                ico.org.uk/make-a-complaint
              </ExternalLink>
              .
            </p>
          </section>

          {/* 10. Marketing */}
          <section>
            <H2>10. Marketing emails</H2>
            <p>
              We may send occasional Bloom tips, product updates and news where we have
              the appropriate permission or lawful basis. Marketing is optional — you can
              unsubscribe using the link in any marketing email or by contacting us at{" "}
              <EmailLink />.
            </p>
            <p className="mt-3">
              Unsubscribing from marketing does not prevent us from sending essential
              service messages, such as subscription, security or account-related
              communications.
            </p>
          </section>

          {/* 11. Cookies */}
          <section>
            <H2>11. Cookies and similar technologies</H2>
            <p>
              Bloom uses essential cookies or similar technologies where necessary to keep
              you signed in, maintain your session and provide core functionality. We do
              not use advertising cookies for third-party advertising.
            </p>
            <p className="mt-3">
              If we introduce analytics or other non-essential cookies or technologies, we
              will update this policy and obtain consent where required by law.
            </p>
          </section>

          {/* 12. Security */}
          <section>
            <H2>12. Security</H2>
            <p>
              We take reasonable technical and organisational measures to protect personal
              information against unauthorised access, loss, misuse or alteration. No online
              service can guarantee absolute security. We will handle any security incidents
              in accordance with applicable legal requirements.
            </p>
          </section>

          {/* 13. Changes */}
          <section>
            <H2>13. Changes to this Privacy Policy</H2>
            <p>
              We may update this policy when Bloom's features, service providers or legal
              obligations change. Where a change is significant, we will take appropriate
              steps to notify users, such as displaying a notice in the app or sending an
              email. The date at the top of this page shows when it was last updated.
            </p>
          </section>

          {/* 14. Contact */}
          <section>
            <H2>14. Contact us</H2>
            <p>
              If you have questions about this policy, your information or your child's
              privacy, please get in touch — we're a small team and we're happy to help.
            </p>
            <p className="mt-3">
              <strong className="text-foreground">Rohan Solutions Ltd, trading as We Built Here</strong>
              <br />
              Email: <EmailLink />
            </p>
          </section>

          {/* Legal footer */}
          <div className="border-t border-border pt-6 text-xs text-muted-foreground">
            This policy is intended to explain our handling of personal information in
            accordance with the UK General Data Protection Regulation (UK GDPR), the
            Data Protection Act 2018 and other applicable data protection law.
          </div>
        </div>
      </main>
    </div>
  );
}

// ─── Local helpers ────────────────────────────────────────────────────────────
function H2({ children }) {
  return (
    <h2 className="mb-3 font-display text-base font-black text-foreground sm:text-lg">
      {children}
    </h2>
  );
}

function H3({ children }) {
  return (
    <h3 className="mb-1.5 font-display text-sm font-black text-foreground sm:text-base">
      {children}
    </h3>
  );
}

function Li({ children }) {
  return (
    <li className="flex gap-2.5">
      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#FF7A45]" />
      <span>{children}</span>
    </li>
  );
}

function EmailLink() {
  return (
    <a
      href="mailto:team@webuilthere.com"
      className="text-[#FF7A45] underline underline-offset-2 transition hover:opacity-75"
    >
      team@webuilthere.com
    </a>
  );
}

function ExternalLink({ href, children }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-[#FF7A45] underline underline-offset-2 transition hover:opacity-75"
    >
      {children}
    </a>
  );
}
